import React, { useState } from 'react';
import { Share2, BookOpen, Sparkles, Flame, Zap, Trophy, Medal, Star, CheckCircle2, ArrowRight } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Artifact, ViewType } from '../types';

interface SummaryViewProps {
  artifacts: Artifact[];
  onNavigate: (view: ViewType) => void;
  onShowToast: (msg: string) => void;
}

const AVATARS = [
  { id: 'fox', emoji: '🦊', name: 'Cáo Nghệ Sĩ', title: 'Họa sĩ thiên tài' },
  { id: 'tiger', emoji: '🐯', name: 'Hổ Dũng Mãnh', title: 'Chiến thần leo tháp' },
  { id: 'owl', emoji: '🦉', name: 'Cú Bác Học', title: 'Thám tử di sản' },
  { id: 'cat', emoji: '🐱', name: 'Mèo Sơn Mài', title: 'Bàn tay khéo léo' },
  { id: 'unicorn', emoji: '🦄', name: 'Kỳ Lân Cổ', title: 'Thần hộ mệnh' },
];

export const SummaryView: React.FC<SummaryViewProps> = ({
  artifacts,
  onNavigate,
  onShowToast,
}) => {
  const [selectedAvatar, setSelectedAvatar] = useState(AVATARS[0]);
  const [playerName, setPlayerName] = useState('An Nguyễn');
  const [isEditingName, setIsEditingName] = useState(false);

  const unlocked = artifacts.filter((a) => a.unlocked);
  const percent = Math.round((unlocked.length / artifacts.length) * 100);

  const handleShare = () => {
    // Fire festive celebration confetti
    try {
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#ffd166', '#ff3366', '#06d6a0', '#8b5cf6'],
      });
    } catch {
      // ignore
    }
    onShowToast('🎉 Đã tạo thẻ Thám Tử Di Sản thành công! Sẵn sàng flex trên Facebook & Instagram Story!');
  };

  return (
    <div className="summary-stage min-h-[85vh] flex items-center justify-center px-4 sm:px-6 py-8 pb-24 animate-fade-in">
      <div className="max-w-4xl w-full">
        <article className="postcard">
          {/* Postcard Header: Gamer Passport Banner */}
          <header className="postcard-header">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
              <div className="space-y-2 max-w-xl">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ffb703]/20 border border-[#ffd166]/60 text-[#ffd166] text-xs font-black uppercase tracking-wider shadow-sm">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>THẺ CĂN CƯỚC THÁM TỬ MỸ THUẬT TEEN</span>
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight text-white tracking-tight">
                  Hồ Sơ Kiện Tướng <br />
                  <span className="text-[#ffd166]">{playerName}</span> ⭐
                </h1>
                <p className="text-xs sm:text-sm text-[#fcefe1] leading-relaxed pt-1">
                  Thành tích phá đảo các trò chơi di sản và hành trình thực tế tại Bảo tàng Mỹ thuật Việt Nam.
                </p>
              </div>

              {/* Avatar Selector Mini HUD */}
              <div className="bg-black/40 border-2 border-[#ffd166]/40 p-3.5 rounded-2xl flex flex-col items-center gap-2 self-start md:self-center shadow-xl backdrop-blur-sm">
                <div className="text-4xl sm:text-5xl w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#ff3366] to-[#ffb703] flex items-center justify-center shadow-lg border-2 border-[#ffd166] animate-mascot-bob">
                  {selectedAvatar.emoji}
                </div>
                <div className="text-center">
                  <span className="text-[11px] font-black text-[#ffd166] block">
                    {selectedAvatar.name}
                  </span>
                  <span className="text-[10px] text-[#e0d0c5]/80 font-semibold block">
                    {selectedAvatar.title}
                  </span>
                </div>

                {/* Avatar change pills */}
                <div className="flex items-center gap-1 pt-1 border-t border-white/10">
                  {AVATARS.map((av) => (
                    <button
                      key={av.id}
                      type="button"
                      onClick={() => setSelectedAvatar(av)}
                      className={`w-7 h-7 rounded-lg text-sm flex items-center justify-center transition-all ${
                        selectedAvatar.id === av.id
                          ? 'bg-[#ffd166] scale-110 shadow-md ring-2 ring-white'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      title={av.name}
                    >
                      {av.emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </header>

          {/* Postcard Body */}
          <div className="summary-body space-y-6">
            {/* Quick Player Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3.5 rounded-2xl bg-[#2b1538] border border-[#ffb703]/30 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#ff3366]/20 border border-[#ff3366]/50 flex items-center justify-center text-[#ff4d6d] shrink-0">
                  <Flame className="w-5 h-5 fill-current animate-bounce" />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-[#ff8fa3] block">Chuỗi streak</span>
                  <strong className="text-base font-black text-white">3 Ngày</strong>
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#2b1538] border border-[#ffb703]/30 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#ffb703]/20 border border-[#ffb703]/50 flex items-center justify-center text-[#ffd166] shrink-0">
                  <Zap className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-[#ffd166] block">Cấp bậc</span>
                  <strong className="text-base font-black text-white">Cấp 5 (850 XP)</strong>
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#2b1538] border border-[#ffb703]/30 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#06d6a0]/20 border border-[#06d6a0]/50 flex items-center justify-center text-[#06d6a0] shrink-0">
                  <Trophy className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-[#06d6a0] block">Xếp hạng tuần</span>
                  <strong className="text-base font-black text-white">Top 31 Server</strong>
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#2b1538] border border-[#ffb703]/30 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#8b5cf6]/20 border border-[#8b5cf6]/50 flex items-center justify-center text-[#c084fc] shrink-0">
                  <Star className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-[#c084fc] block">Danh hiệu</span>
                  <strong className="text-sm font-black text-white truncate block">Thám Tử Tập Sự</strong>
                </div>
              </div>
            </div>

            {/* Big Stamp Progress Counter */}
            <div className="big-progress">
              <div className="flex items-center gap-4">
                <span className="progress-number">
                  {unlocked.length}/{artifacts.length}
                </span>
                <div>
                  <h2 className="text-lg sm:text-xl font-black text-white">
                    Bảo vật Quốc gia đã gom trọn 🎒
                  </h2>
                  <p className="text-xs sm:text-sm text-[#e0d0c5] mt-0.5">
                    Đạt {percent}% tiến trình Bộ Sưu Tập trên tài khoản của bạn
                  </p>
                </div>
              </div>

              <div className="w-full sm:w-56 space-y-1.5">
                <div className="flex justify-between text-xs font-bold text-[#ffd166]">
                  <span>Tiến trình</span>
                  <span>{percent}%</span>
                </div>
                <div className="w-full h-3 bg-black/50 rounded-full overflow-hidden border border-[#ffb703]/40 p-0.5 shadow-inner">
                  <div
                    className="h-full bg-gradient-to-r from-[#ff3366] via-[#ffb703] to-[#06d6a0] transition-all duration-500 rounded-full"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Mini Stamps Gallery */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <strong className="text-[#ffd166] font-black uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  CÁC CON TEM ĐÃ MỞ KHÓA ({unlocked.length})
                </strong>
                <button
                  onClick={() => onNavigate('journey')}
                  className="text-[#ffd166] hover:underline font-bold flex items-center gap-1"
                >
                  <span>Mở Balo Tem</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="flex items-center gap-3.5 overflow-x-auto py-2">
                {unlocked.map((art) => (
                  <div
                    key={art.id}
                    onClick={() => onNavigate('journey')}
                    className="w-24 sm:w-28 aspect-[4/5] shrink-0 bg-gradient-to-br from-[#2f163d] to-[#1c0d25] p-2.5 rounded-2xl border-2 border-[#ffb703]/40 shadow-lg cursor-pointer hover:border-[#ffd166] hover:scale-105 transition-all"
                  >
                    <img
                      src={art.stamp}
                      alt={art.title}
                      className="w-full h-full object-contain filter drop-shadow-md"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* 3 Games Achievements */}
            <div className="space-y-3">
              <strong className="text-xs text-[#ffd166] font-black uppercase tracking-wider block">
                THÀNH TÍCH 03 TRÒ CHƠI DI SẢN
              </strong>

              <div className="achievements-grid">
                <div className="achievement">
                  <span className="text-[10px] font-black text-[#ffd166] uppercase tracking-wider block">
                    🏰 LEO THÁP MỸ THUẬT
                  </span>
                  <strong>Tầng cao nhất 07</strong>
                  <small className="text-[#fcefe1]">31 câu đố chính xác · Hạng Vàng</small>
                </div>

                <div className="achievement">
                  <span className="text-[10px] font-black text-[#06d6a0] uppercase tracking-wider block">
                    🎨 VẼ VÀ ĐOÁN
                  </span>
                  <strong>1.260 điểm</strong>
                  <small className="text-[#fcefe1]">Hạng 31 toàn tuần · Combo x5</small>
                </div>

                <div className="achievement">
                  <span className="text-[10px] font-black text-[#c084fc] uppercase tracking-wider block">
                    🧩 GHÉP TRANH TỐC ĐỘ
                  </span>
                  <strong>04 tranh hoàn tất</strong>
                  <small className="text-[#fcefe1]">Kỷ lục nhanh nhất 01:15</small>
                </div>
              </div>
            </div>

            {/* Action CTAs */}
            <div className="pt-4 flex flex-wrap items-center gap-3.5 border-t border-white/10">
              <button
                onClick={handleShare}
                className="gf-button text-sm py-3 px-6 shadow-xl flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                <span>📸 Khoe Thẻ Lên Story / Bạn Bè</span>
              </button>

              <button
                onClick={() => onNavigate('journey')}
                className="gf-button gf-button-secondary text-sm py-3 px-6 flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4 text-[#ffd166]" />
                <span>Xem Balo Bộ Sưu Tập</span>
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};
